import { isObject } from 'lodash';
import { guards } from '@shared/helpers/typeguards.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';

/** Snackbar id for {@link CLIPBOARD_WRITE_DENIED_MESSAGE}. */
export const CLIPBOARD_WRITE_DENIED_MESSAGE_ID = 'clipboard.write_denied';

/**
 * Shown when the browser refuses `navigator.clipboard.writeText`
 * (`NotAllowedError` / write permission denied, including an unfocused document).
 */
export const CLIPBOARD_WRITE_DENIED_MESSAGE =
  'Copy was not allowed. Allow clipboard access in the browser, or focus this page and try again.';

const CLIPBOARD_WRITE_PERMISSION_DENIED = 'write permission denied';

/**
 * Recoverable clipboard write failure. Callers should show
 * {@link CLIPBOARD_WRITE_DENIED_MESSAGE} and must not report it as a crash.
 */
export class ClipboardWriteDeniedError extends Error {
  constructor(message: string = CLIPBOARD_WRITE_DENIED_MESSAGE) {
    super(message);
    this.name = 'ClipboardWriteDeniedError';
  }
}

type ErrorLike = {
  name?: unknown;
  message?: unknown;
};

const isErrorLike = (value: unknown): value is ErrorLike =>
  // DOMException is an object, but not a plain object, so guards.is.object does not match it.
  guards.is.defined(value) && typeof value === 'object';

/**
 * True when a clipboard write was refused by the browser.
 * Matches {@link ClipboardWriteDeniedError}, `NotAllowedError`, and the
 * Edge/Chrome message "Write permission denied".
 */
export const isClipboardWriteDenied = (error: unknown): boolean => {
  if (error instanceof ClipboardWriteDeniedError) return true;
  if (!isErrorLike(error)) return false;
  if (error.name === 'NotAllowedError') return true;
  return (
    guards.is.string(error.message) &&
    error.message.toLowerCase().includes(CLIPBOARD_WRITE_PERMISSION_DENIED)
  );
};

export type ClipboardWriteResult =
  { status: 'copied' } | { status: 'denied'; message: string };

/**
 * Writes plain text to the system clipboard.
 * Browser write denial is returned as `{ status: 'denied' }` with
 * {@link CLIPBOARD_WRITE_DENIED_MESSAGE} so callers can show feedback without
 * logging an unexpected crash. Other failures are rethrown.
 */
export const writeClipboardText = async (
  text: string,
): Promise<ClipboardWriteResult> => {
  try {
    await navigator.clipboard.writeText(text);
    return { status: 'copied' };
  } catch (error) {
    if (!isClipboardWriteDenied(error)) throw error;
    return { status: 'denied', message: CLIPBOARD_WRITE_DENIED_MESSAGE };
  }
};

export type ClipboardWriteFeedback = {
  onCopied: () => void;
  onDenied: (message: string) => void;
  onUnexpected: (error: unknown) => void;
};

/**
 * Writes text and routes the outcome to feedback callbacks.
 * Permission denial calls `onDenied` only — not `onUnexpected` — so it is not
 * logged as an application crash.
 */
export const copyTextToClipboard = async (
  text: string,
  feedback: ClipboardWriteFeedback,
): Promise<void> => {
  try {
    const result = await writeClipboardText(text);
    switch (result.status) {
      case 'copied':
        feedback.onCopied();
        return;
      case 'denied':
        feedback.onDenied(result.message);
        return;
      default:
        return assertUnreachable(result);
    }
  } catch (error) {
    feedback.onUnexpected(error);
  }
};

export const getClipboardObject = async <T extends object = object>(): Promise<
  T | undefined
> => {
  const text = await navigator.clipboard.readText();
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined;
  }
};

/**
 * Serializes `object` and writes it to the clipboard.
 * Throws {@link ClipboardWriteDeniedError} when the browser denies the write,
 * instead of masking that failure as invalid data.
 * @throws {Error} When `object` is not an object.
 * @throws {ClipboardWriteDeniedError} When the browser denies the write.
 */
export const setClipboardObject = async <T extends object>(object: T) => {
  if (!isObject(object)) {
    throw new Error('data is not an object');
  }

  const text = JSON.stringify(object);
  const result = await writeClipboardText(text);
  switch (result.status) {
    case 'copied':
      return text;
    case 'denied':
      throw new ClipboardWriteDeniedError(result.message);
    default:
      return assertUnreachable(result);
  }
};
